import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { SCREEN_CODE_BY_PATH } from '~/constants/config/subsidiary-screen-paths';
import { getSubsidiaryScreens } from '~/features/organization/services/screen-access.service';
import type { TenantScreen } from '~/features/organization/types/screen.types';
import { useAuthStore } from '~/store/auth.store';

const FIVE_MINUTES = 5 * 60 * 1000;

/** True when `pathname` is `route` itself or a nested route under it. */
function isUnderRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(route + '/');
}

/**
 * Collects the codes and routes of every screen switched off for the subsidiary. Disabling a
 * parent cascades to its children, so a child counts as off when any ancestor is off.
 */
function collectDisabled(screens: TenantScreen[]): { codes: Set<string>; routes: string[] } {
  const byId = new Map(screens.map((s) => [s.id, s]));
  const isOff = (screen: TenantScreen, seen = new Set<string>()): boolean => {
    if (!screen.isEnabledForSubsidiary) return true;
    if (!screen.parentId || seen.has(screen.id)) return false;
    seen.add(screen.id);
    const parent = byId.get(screen.parentId);
    return parent ? isOff(parent, seen) : false;
  };
  const off = screens.filter((s) => isOff(s));
  return {
    codes: new Set(off.map((s) => s.code)),
    routes: off.map((s) => s.route).filter((r) => r.length > 0),
  };
}

/**
 * Subsidiary-level screen enablement from GET /screens/subsidiary/:subId — the switches on the
 * Navigation Access page. A screen switched off there must be neither visible nor reachable in
 * that subsidiary, for tenant admins and members alike; super admins are exempt so they can
 * always reach Navigation Access to switch screens back on. Screens the subsidiary list doesn't
 * mention, and the loading/error states, count as enabled (fail open — the role/permission and
 * /screens/navigation checks still apply on top). Shares its query key with the Navigation
 * Access page, so toggling a screen there updates the sidebar and guards immediately.
 */
export function useSubsidiaryScreens() {
  const tenantId = useAuthStore((s) => s.user?.activeTenantId ?? '');
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  const query = useQuery({
    queryKey: queryKeys.screens.subsidiary(tenantId),
    queryFn: () => getSubsidiaryScreens(tenantId),
    enabled: !!tenantId && !isSuperAdmin,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const disabled = !isSuperAdmin && query.data ? collectDisabled(query.data) : null;

  /** Whether the screen with this `code` is enabled for the active subsidiary. */
  const isCodeEnabled = (code: string): boolean => !disabled?.codes.has(code);

  /**
   * Whether `pathname` is reachable: blocked when it sits under a disabled screen's route, or
   * under a frontend path aliased to a disabled screen code (see `SCREEN_CODE_BY_PATH`).
   */
  const isPathEnabled = (pathname: string): boolean => {
    if (!disabled) return true;
    if (disabled.routes.some((route) => isUnderRoute(pathname, route))) return false;
    return !Object.entries(SCREEN_CODE_BY_PATH).some(
      ([path, code]) => disabled.codes.has(code) && isUnderRoute(pathname, path),
    );
  };

  return {
    isCodeEnabled,
    isPathEnabled,
    /** True only on the first fetch for a non-super-admin; guards wait on it to avoid a flash. */
    isLoading: query.isLoading,
  };
}
